import axios from 'axios';

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  withCredentials: true
});

api.interceptors.request.use((config) => {
  const isAdminArea = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');
  const token = isAdminArea
    ? localStorage.getItem('adminToken') || localStorage.getItem('fosen_access_token')
    : localStorage.getItem('fosen_access_token') || localStorage.getItem('adminToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const authApi = {
  register: (payload) => api.post('/auth/register', payload),
  registerAdmin: (payload) => api.post('/auth/admin/register', payload),
  login: (payload) => api.post('/auth/login', payload),
  adminLogin: (payload) => api.post('/auth/admin/login', payload),
  adminProfile: () => api.get('/admin/profile'),
  logout: (payload) => api.post('/auth/admin/logout', payload),
  me: () => api.get('/auth/me'),
  refresh: (refreshToken) => api.post('/auth/refresh', { refreshToken }),
  addAddress: (payload) => api.post('/auth/addresses', payload),
  updateProfile: (payload) => api.patch('/auth/me', payload),
  requestOtp: (payload) => api.post('/auth/otp/request', payload),
  verifyOtp: (payload) => api.post('/auth/otp/verify', payload),
  forgotPassword: (payload) => api.post('/auth/forgot-password', payload),
  resetPassword: (payload) => api.post('/auth/reset-password', payload)
};

export const productApi = {
  list: (params) => api.get('/products', { params }),
  get: (id) => api.get(`/products/${id}`),
  create: (payload) => api.post('/products', payload),
  update: (id, payload) => api.patch(`/products/${id}`, payload),
  delete: (id) => api.delete(`/products/${id}`),
  bulkDelete: (ids) => api.post('/products/bulk-delete', { ids }),
  updateStock: (id, stock, variantId) => api.patch(`/products/${id}/stock`, { stock, variantId })
};

export const categoryApi = {
  list: (params) => api.get('/categories', { params }),
  get: (id) => api.get(`/categories/${id}`)
};

export const cartApi = {
  get: () => api.get('/cart'),
  addItem: (payload) => api.post('/cart/items', payload),
  updateItem: (itemId, payload) => api.patch(`/cart/items/${itemId}`, payload),
  removeItem: (itemId) => api.delete(`/cart/items/${itemId}`),
  applyCoupon: (code) => api.post('/cart/coupon', { code })
};

export const orderApi = {
  checkDelivery: (pincode) => api.get('/orders/delivery/check', { params: { pincode } }),
  calculate: (payload) => api.post('/orders/checkout/calculate', payload),
  createPaymentOrder: (payload) => api.post('/orders/payments/razorpay-order', payload),
  verifyPayment: (payload) => api.post('/orders/payments/verify', payload),
  create: (payload) => api.post('/orders', payload),
  mine: () => api.get('/orders/mine'),
  getMine: (orderId) => api.get(`/orders/mine/${orderId}`),
  requestReturn: (orderId, payload) => api.post(`/orders/${orderId}/returns`, payload),
  verifyRemainingPayment: (orderId, payload) => api.post(`/orders/mine/${orderId}/remaining-payment/verify`, payload),
  cancel: (orderId, payload) => api.patch(`/orders/mine/${orderId}/cancel`, payload),
  downloadReceipt: (orderId) => api.get(`/orders/mine/${orderId}/receipt`, { responseType: 'blob' })
};

export const adminApi = {
  dashboard: () => api.get('/admin/dashboard'),
  saveDeliveryCharge: (payload) => api.post('/admin/delivery-charges', payload),
  listDeliveryZones: () => api.get('/admin/delivery-zones'),
  saveDeliveryZone: (payload) => api.post('/admin/delivery-zones', payload),
  updateDeliveryZone: (id, payload) => api.patch(`/admin/delivery-zones/${id}`, payload),
  deleteDeliveryZone: (id) => api.delete(`/admin/delivery-zones/${id}`),
  bulkDeleteDeliveryZones: (ids) => api.post('/admin/delivery-zones/bulk-delete', { ids }),
  createCoupon: (payload) => api.post('/admin/coupons', payload),
  updateOrderStatus: (orderId, payload) => api.patch(`/orders/${orderId}/status`, payload),
  updateOrderPayment: (orderId, payload) => api.patch(`/orders/${orderId}/payment`, payload),
  initiateDeliveryCompletion: (orderId) => api.post(`/orders/${orderId}/delivery/initiate`),
  verifyRemainingPayment: (orderId, payload) => api.post(`/orders/${orderId}/delivery/remaining-payment/verify`, payload),
  verifyOrderHandover: (orderId, payload) => api.patch(`/orders/${orderId}/handover`, payload),
  listPaymentHistory: () => api.get('/admin/payments/history'),
  listStores: () => api.get('/admin/stores?all=true'),
  createStore: (payload) => api.post('/admin/stores', payload),
  updateStore: (id, payload) => api.patch(`/admin/stores/${id}`, payload),
  deleteStore: (id) => api.delete(`/admin/stores/${id}`),
  bulkDeleteStores: (ids) => api.post('/admin/stores/bulk-delete', { ids }),
  listReturns: (params) => api.get('/admin/returns', { params }),
  updateReturn: (id, payload) => api.patch(`/admin/returns/${id}`, payload)
};

export const storeApi = {
  list: () => api.get('/stores')
};

export const couponApi = {
  list: () => api.get('/coupons')
};
